import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getServices } from '../services/api';
import { useMerchant } from '../contexts/MerchantContext';

function ServiceList() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { merchantSlug } = useParams();
  const { merchant } = useMerchant();
  
  // 調試信息
  console.log('ServiceList - merchant:', merchant);
  console.log('ServiceList - merchant.name:', merchant?.name);

  // 簡單的一次性載入，沒有依賴
  useEffect(() => {
    const loadServices = async () => {
      if (!merchantSlug) return;
      
      try {
        setLoading(true);
        const response = await getServices(merchantSlug);
        setServices(response.data.results || response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, []); // 空依賴陣列，只執行一次

  const getServiceIcon = (serviceName) => {
    const iconMap = {
      '基礎護理': 'spa',
      '深層清潔': 'air',
      '保濕導入': 'local_florist',
      '抗痘調理': 'bubble_chart',
      '美甲': 'colorize',
      '按摩': 'healing',
      '臉部護理': 'face',
      '身體護理': 'self_care'
    };
    
    // Find matching icon or use default
    for (const [key, icon] of Object.entries(iconMap)) {
      if (serviceName.includes(key)) return icon;
    }
    return 'spa'; // default icon
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-primary text-lg">載入中...</div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex items-center justify-between whitespace-nowrap px-4 py-3">
        <div className="flex items-center gap-2 text-text-primary">
          <div className="text-primary">
            <svg className="size-6" fill="currentColor" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path clipRule="evenodd" d="M24 4H42V17.3333V30.6667H24V44H6V30.6667V17.3333H24V4Z" fillRule="evenodd"></path>
            </svg>
          </div>
          <h2 className="text-text-primary text-lg font-bold leading-tight tracking-[-0.015em]">
            {merchant?.name || '體驗系統'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/${merchantSlug}/bookings`} className="flex items-center justify-center rounded-full h-10 w-10 bg-background-light dark:bg-background-dark text-text-primary border border-gray-200 dark:border-gray-700 hover:bg-primary/10 transition-colors">
            <span className="material-symbols-outlined">person</span>
          </Link>
        </div>
      </header>

      {/* Title Section */}
      <div className="flex flex-wrap justify-between gap-3 p-4">
        <div className="flex min-w-full flex-col gap-2">
          <p className="text-text-primary text-4xl font-black leading-tight tracking-[-0.033em]">
            線上體驗{merchant && merchant.name ? ` - ${merchant.name}` : ''}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-base font-normal leading-normal">煥然一新，從這裡開始</p>
        </div>
      </div>

      {/* Hero Image */}
      <div className="p-4">
        <div className="w-full bg-center bg-no-repeat bg-cover flex flex-col justify-end overflow-hidden rounded-xl min-h-48" 
             style={{backgroundImage: "url('https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1926&q=80')"}}>
        </div>
      </div>

      {/* Services Section */}
      <div className="flex flex-col gap-2 p-4">
        <p className="text-text-primary text-lg font-bold">選擇服務項目</p>
        {services.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
            <p>目前沒有可用的服務項目</p>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700 border-t border-b border-gray-200 dark:border-gray-700">
            {services.map(service => (
            <Link 
              key={service.id}
              to={`/${merchantSlug}/booking/${service.id}`}
              className="flex items-center gap-4 bg-transparent px-2 py-4 justify-between hover:bg-primary/10 rounded-lg transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div className="text-secondary flex items-center justify-center rounded-lg bg-secondary/20 shrink-0 size-12">
                  <span className="material-symbols-outlined">{getServiceIcon(service.name)}</span>
                </div>
                <div className="flex flex-col justify-center">
                  <p className="text-text-primary text-base font-medium leading-normal">{service.name}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-normal leading-normal">
                    {service.description} • {service.duration_minutes}分鐘 • NT${service.price}
                  </p>
                </div>
              </div>
              <div className="shrink-0">
                <div className="text-text-primary flex size-7 items-center justify-center">
                  <span className="material-symbols-outlined">chevron_right</span>
                </div>
              </div>
            </Link>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 left-0 right-0 p-4 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700">
        <Link 
          to={`/${merchantSlug}/bookings`}
          className="w-full flex items-center justify-center rounded-lg h-12 bg-gray-200 dark:bg-gray-700 text-text-primary text-base font-bold leading-normal tracking-wide hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          查看我的體驗記錄
        </Link>
      </div>
    </>
  );
}

export default ServiceList;